
// create a js Date object from a german date string like "24.12.2020"
const dateFromGermanString = (input) => {
	if (! input) return false;

	const parts = input.split('.');

	if (parts.length !== 3) {
		return false;
	}

	return new Date(parts[ 2 ], (parts[ 1 ] - 1), parts[ 0 ]);
};

const dateToGermanString = (date) => {
	if (! (date instanceof Date)) {
		return '';
	}
	
	return ((date.getDate() < 10) ? '0' + date.getDate() : date.getDate())
	+ '.' + 
	(((date.getMonth() + 1) < 10) ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1))
	+ '.' +
	date.getFullYear();
};

// create Date object from year and month
// month is zero based, p. e. 0 = January
// month empty and included false => generates date like 01.01.2000
// month empty and included true  => generates date like  31.12.2000
const dateFromYearMonth = (year, month = false, yearIncluded = false) => {
	if (! year) {
		return false;
	}
	if (month) {
		return new Date(year, month);
	}

	if (yearIncluded) {
		return new Date(year, 11, 31);
	}

	return new Date(year, 0);
};

const getDatesFromEntry = (entry) => {
	const fromDate = dateFromGermanString(entry.dateFrom);
	const toDate = dateFromGermanString(entry.dateTo);
	if (fromDate && toDate) {
		return [ fromDate, toDate ];
	} else if (fromDate) {
		return [ fromDate, false ];
	}

	// try parsing from headline
	// format: "09.11.2020 - 22.02.2021"
	let matches = entry.headline.match(/(\d{2}\.\d{2}\.\d{4}) ?- ?(\d{2}\.\d{2}\.\d{4})/);
	if (matches) {
		return [ dateFromGermanString(matches[ 1 ]), dateFromGermanString(matches[ 2 ]) ];
	}

	// format: "14.-15.10.2021" AND "14./15.10.2021"
	matches = entry.headline.match(/(\d{2})\. ?-?\/? ?(\d{2})\.(\d{2}\.\d{4})/);
	if (matches) {
		const fullFrom = matches[ 1 ] + '.' + matches[ 3 ];
		const fullTo = matches[ 2 ] + '.' + matches[ 3 ];
		return [ dateFromGermanString(fullFrom), dateFromGermanString(fullTo) ];
	}

	// format: "09.11.2020" (fromDate only)
	matches = entry.headline.match(/\d{2}\.\d{2}\.\d{4}/);
	if (matches) {
		return [ dateFromGermanString(matches[ 0 ]), false ];
	}

	// format: "09.11.20" (fromDate only)
	matches = entry.headline.match(/(\d{2}\.\d{2}\.)(\d{2})/);
	if (matches) {
		const fullDate = matches[ 1 ] + '20' + matches[ 2 ];
		return [ dateFromGermanString(fullDate), false ];
	}

	// no match possible
	return [ false, false ];
};

export const hydrateDateToEntry = (entry) => {
	if (entry.dateFrom) {
		return entry;
	}

	const [ dateFrom, dateTo ] = getDatesFromEntry(entry);
	if (dateFrom) {
		entry.dateFrom = dateToGermanString(dateFrom);
	}
	if (dateTo) {
		entry.dateTo = dateToGermanString(dateTo);
	}

	return entry;
};

export const getFirstYearFromInputData = (inputData) => {
	let year = 9999;
	for (let i = 0; i < inputData.length; i++) {
		const date = dateFromGermanString(inputData[ i ].dateFrom);
		if (date && date.getFullYear() < year) {
			year = date.getFullYear();
		}
	}
	return year;
};

export const getLastYearFromInputData = (inputData) => {
	let year = 0;
	for (let i = 0; i < inputData.length; i++) {
		const date = dateFromGermanString(inputData[ i ].dateFrom);
		if (date && date.getFullYear() > year) {
			year = date.getFullYear();
		}
	}
	return year;
};

// inputData is expected to have a "dateFrom" and "dateTo" property (may be an empty string)
// textInput will be searched inside "headline", "text" and "location" property
export const filter = (
	textInput = "",
	fromYear = false,
	fromMonth = false, // zero indexed ( 0 = January )
	toYear = false,
	toMonth = false, // zero indexed ( 0 = January )
	inputData = []) => {
	let filteredInput = inputData.slice();

	if (textInput) {
		const textInputLowerCase = textInput.toLocaleLowerCase();
		filteredInput = filteredInput.filter(entry =>
			entry.headline.toLocaleLowerCase().includes(textInputLowerCase) ||
			entry.text.toLocaleLowerCase().includes(textInputLowerCase) ||
			entry.location.toLocaleLowerCase().includes(textInputLowerCase)
		);
	}

	const fromDate = dateFromYearMonth(fromYear, fromMonth, false);
	const toDate = dateFromYearMonth(toYear, toMonth, true);

	if (fromDate && toDate) {
		// search between to dates
		filteredInput = filteredInput.filter(entry => {
			const [ entryDateFrom, entryDateTo ] = getDatesFromEntry(entry);
			if (entryDateFrom && entryDateTo) {
				return entryDateFrom.getTime() >= fromDate.getTime()
					&& entryDateTo.getTime() <= toDate.getTime();
			} else if (entryDateFrom && ! entryDateTo) {
				return entryDateFrom.getTime() >= fromDate.getTime()
					&& entryDateFrom.getTime() <= toDate.getTime();
			}
			return false;
		});
	} else if (fromDate && ! toDate) {
		// search entries newer than fromDate
		filteredInput = filteredInput.filter(entry => {
			const [ entryDateFrom ] = getDatesFromEntry(entry);
			if (entryDateFrom) {
				return entryDateFrom.getTime() >= fromDate.getTime();
			}
			return false;
		});
	} else if (! fromDate && toDate) {
		// search entries older or equal than toDate
		filteredInput = filteredInput.filter(entry => {
			const [ entryDateFrom, entryDateTo ] = getDatesFromEntry(entry);
			if (entryDateTo) {
				return entryDateTo.getTime() <= toDate.getTime();
			} else if (entryDateFrom) {
				return entryDateFrom.getTime() <= toDate.getTime();
			}
			return false;
		});
	}

	return filteredInput;
};

export const sortByFromDate = (inputData, asc = false) => {
	return inputData.sort((entryA, entryB) => {
		const [ entryAFrom ] = getDatesFromEntry(entryA);
		const [ entryBFrom ] = getDatesFromEntry(entryB);

		// sort entries without fromDate to the end of the list
		if (! entryAFrom && ! entryBFrom) {
			return 0;
		}
		if (! entryAFrom) {
			return 1;
		}
		if (! entryBFrom) {
			return -1;
		}
		if (asc) {
			return entryAFrom.getTime() - entryBFrom.getTime();
		}
		return entryBFrom.getTime() - entryAFrom.getTime();
	});
};
